import { Student } from '@/types';

// Professional photo collections for different genders
const PHOTO_COLLECTIONS = {
  male: [
    'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=400&fit=crop&crop=faces&auto=format&q=80',
    'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop&crop=faces&auto=format&q=80',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=faces&auto=format&q=80',
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop&crop=faces&auto=format&q=80',
    'https://images.unsplash.com/photo-1618641986557-1ecd230959aa?w=400&h=400&fit=crop&crop=faces&auto=format&q=80',
    'https://images.unsplash.com/photo-1531891437562-4301cf35b7e4?w=400&h=400&fit=crop&crop=faces&auto=format&q=80',
    'https://images.unsplash.com/photo-1552374196-c4e7ffc6e126?w=400&h=400&fit=crop&crop=faces&auto=format&q=80',
    'https://images.unsplash.com/photo-1544724107-6d5c4caaff30?w=400&h=400&fit=crop&crop=faces&auto=format&q=80'
  ],
  female: [
    'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=400&fit=crop&crop=faces&auto=format&q=80',
    'https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&h=400&fit=crop&crop=faces&auto=format&q=80',
    'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=400&h=400&fit=crop&crop=faces&auto=format&q=80',
    'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=faces&auto=format&q=80',
    'https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?w=400&h=400&fit=crop&crop=faces&auto=format&q=80',
    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop&crop=faces&auto=format&q=80',
    'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400&h=400&fit=crop&crop=faces&auto=format&q=80',
    'https://images.unsplash.com/photo-1534751516642-a1af1ef26a56?w=400&h=400&fit=crop&crop=faces&auto=format&q=80'
  ]
} as const;

// Fallback photos if main photos fail to load
const FALLBACK_PHOTOS = {
  male: 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=400&h=400&fit=crop&crop=faces&auto=format&q=80',
  female: 'https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?w=400&h=400&fit=crop&crop=faces&auto=format&q=80'
};

class PhotoService {
  private static instance: PhotoService;
  private photoAssignments: Map<string, string> = new Map();
  private usedPhotos: Set<string> = new Set();

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  public static getInstance(): PhotoService {
    if (!PhotoService.instance) {
      PhotoService.instance = new PhotoService();
    }
    return PhotoService.instance;
  }

  public getStudentPhoto(student: Student): string {
    // Return cached photo if exists
    const cachedPhoto = this.photoAssignments.get(student.id);
    if (cachedPhoto) {
      return cachedPhoto;
    }

    // Get appropriate photo collection based on gender
    const collection = PHOTO_COLLECTIONS[student.gender as keyof typeof PHOTO_COLLECTIONS] || PHOTO_COLLECTIONS.male;
    
    // Find an unused photo
    const availablePhotos = collection.filter(photo => !this.usedPhotos.has(photo));
    
    // If no unused photos are available, use fallback
    if (availablePhotos.length === 0) {
      const fallback = FALLBACK_PHOTOS[student.gender as keyof typeof FALLBACK_PHOTOS] || FALLBACK_PHOTOS.male;
      this.photoAssignments.set(student.id, fallback);
      return fallback;
    }

    // Randomly select from available photos
    const randomIndex = Math.floor(Math.random() * availablePhotos.length);
    const selectedPhoto = availablePhotos[randomIndex];
    
    // Mark photo as used and cache assignment
    this.usedPhotos.add(selectedPhoto);
    this.photoAssignments.set(student.id, selectedPhoto);
    
    return selectedPhoto;
  }

  public clearCache(): void {
    this.photoAssignments.clear();
    this.usedPhotos.clear();
  }
}

export const photoService = PhotoService.getInstance();